import {
  HttpErrorResponse,
  HttpEvent,
  HttpInterceptorFn,
  HttpRequest
} from '@angular/common/http';
import { inject } from '@angular/core';
import { AppShellService } from '../services/app-shell.service';
import {
  BehaviorSubject,
  Observable,
  throwError,
  of
} from 'rxjs';
import {
  catchError,
  filter,
  finalize,
  switchMap,
  take,
  shareReplay
} from 'rxjs/operators';
import { StorageService } from '../services/storage.service';
import { AuthService } from '../services/auth.service';
import { RefreshTokenResponseI } from '../models/accounts.model';
import { CrashMonitorService } from '../services/crash-monitor.service';

let isRefreshing = false;
let refreshToken$!: Observable<RefreshTokenResponseI>;  // keep reference to refresh call
const refreshTokenSubject = new BehaviorSubject<string | null>(null);

export const tokenInterceptorInterceptor: HttpInterceptorFn = (req, next) => {
  const storageService = inject(StorageService);
  const appShell = inject(AppShellService);
  const authService = inject(AuthService);
  const crashMonitor = inject(CrashMonitorService);

  const token = storageService.get('userToken');
  if (token) {
    req = req.clone({
      setHeaders: {
        Authorization: `${token}`
      }
    });
  }

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      crashMonitor.logApiError(error, req.url);

      if (error.status === 429 || error.status === 403) {
        storageService.clearAll();
        appShell.redirectToLogin();
        return throwError(() => error);
      } else if (error.status === 401) {
        // In the app webview the mobile app owns the session, and the backend rotates the refresh
        // token on every refresh: refreshing here would invalidate the app's copy and log it out.
        // Hand control back to the app instead.
        if (appShell.isEmbedded) {
          storageService.clearAll();
          appShell.redirectToLogin();
          return throwError(() => error);
        }
        return handle401Error(req, next, storageService, authService, appShell);
      }
      return throwError(() => error);
    })
  );
};

function handle401Error(
  request: HttpRequest<any>,
  next: (req: HttpRequest<any>) => Observable<HttpEvent<any>>,
  storageService: StorageService,
  authService: AuthService,
  appShell: AppShellService
): Observable<HttpEvent<any>> {

  if (!isRefreshing) {
    isRefreshing = true;
    refreshTokenSubject.next(null);

    const tokenData = {
      accessToken: storageService.get('userToken')?.replace('Bearer ', ''),
      refreshToken: storageService.get('refreshToken')
    };

    // Create ONE refresh request observable and share it
    refreshToken$ = authService.getRefreshToken(tokenData).pipe(
      shareReplay(1), // replay the result to all subscribers
      finalize(() => { isRefreshing = false; }) // reset flag when done
    );

    return refreshToken$.pipe(
      switchMap((data: RefreshTokenResponseI) => {
        storageService.add('userToken', data.accessToken);
        storageService.add('refreshToken', data.refreshToken);
        refreshTokenSubject.next(data.accessToken);

        const clonedReq = request.clone({
          setHeaders: {
            Authorization: `${data.accessToken}`
          }
        });
        return next(clonedReq);
      }),
      catchError(err => {
        storageService.clearAll();
        appShell.redirectToLogin();
        return throwError(() => err);
      })
    );

  } else {
    // Already refreshing → wait until new token is published
    return refreshTokenSubject.pipe(
      filter(token => token !== null),
      take(1),
      switchMap(token => {
        const clonedReq = request.clone({
          setHeaders: {
            Authorization: `${token}`
          }
        });
        return next(clonedReq);
      })
    );
  }
}
